import React from 'react';
import { NavLink, Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage, { PageProvider } from '@dashboard/hooks/usePage';
import { getPaymentsLabel, getPaymentsSlug } from '@dashboard/services/getProductType';

import Card from '@dashboard/components/Card';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';
import TabNavigation from '@dashboard/components/TabNavigation';

import PageDetailHeader from '../PageEdit/components/PageDetailHeader';

import PageAnalytics from '../PageOverview/scenes/PageAnalytics';
import PageCustomers from '../PageOverview/scenes/PageCustomers';
import PagePayments from '../PageOverview/scenes/PagePayments';
import PageSubmissions from '../PageOverview/scenes/PageSubmissions';
import PageSubscriptions from '../PageOverview/scenes/PageSubscriptions';

const PageResults = () => {
  const match = useRouteMatch();
  const { seller } = useAuth();

  const { page, isLoading, isError } = usePage();

  if (isLoading) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }

  if (isError) {
    return <Card>Something went wrong</Card>;
  }

  return (
    <>
      <PageDetailHeader>
        <PageDetailHeader.Title>
          <div className="flex items-center gap-x-2">
            {page.name} {page.status === 'archived' && <StatusBadge status={page.status} />}
          </div>
        </PageDetailHeader.Title>
      </PageDetailHeader>

      <TabNavigation>
        <NavLink
          to={`${match.url}/${getPaymentsSlug(page)}`}
          className={`p-0 m-0 border-b-2 border-transparent transition-colors focus:outline-none focus:border-chocolate-600 ring-0 font-medium hover:text-chocolate-600`}
          activeClassName="border-chocolate-600 text-chocolate-600"
        >
          {getPaymentsLabel(page)}
        </NavLink>

        <NavLink
          to={`${match.url}/customers`}
          className={`p-0 m-0 border-b-2 border-transparent transition-colors focus:outline-none focus:border-chocolate-600 ring-0 font-medium hover:text-chocolate-600`}
          activeClassName="border-chocolate-600 text-chocolate-600"
        >
          {page.type === 'checkout' && 'Customers'}
          {page.type === 'event' && 'Customers'}
          {page.type === 'form' && 'Leads'}
        </NavLink>

        <NavLink
          to={`${match.url}/analytics`}
          className={`p-0 m-0 border-b-2 border-transparent transition-colors focus:outline-none focus:border-chocolate-600 ring-0 font-medium hover:text-chocolate-600`}
          activeClassName="border-chocolate-600 text-chocolate-600"
        >
          Analytics
        </NavLink>
      </TabNavigation>

      <div className="m-4">
        <Switch>
          <Route exact path={`/pages/${match.params.id}/results/analytics`}>
            <PageAnalytics page={page} product={page?.product} seller={seller} />
          </Route>

          <Route exact path={`/pages/${match.params.id}/results/customers`}>
            <PageCustomers page={page} />
          </Route>

          {page.type === 'form' && (
            <>
              <Redirect
                exact
                from={`/pages/${match.params.id}/results`}
                to={`/pages/${match.params.id}/results/submissions`}
              />

              <Route exact path={`/pages/${match.params.id}/results/submissions`}>
                <PageSubmissions page={page} />
              </Route>
            </>
          )}

          {page.type === 'event' && (
            <>
              <Redirect
                exact
                from={`/pages/${match.params.id}/results`}
                to={`/pages/${match.params.id}/results/payments`}
              />

              <Route exact path={`/pages/${match.params.id}/results/payments`}>
                <PagePayments page={page} />
              </Route>

              <Route exact path={`/pages/${match.params.id}/results/customers`}>
                <PageCustomers page={page} />
              </Route>

              <Route exact path={`/pages/${match.params.id}/results/analytics`}>
                <PageAnalytics page={page} />
              </Route>
            </>
          )}

          {page.type === 'checkout' && (
            <>
              {page.product.type === 'charge' && (
                <Redirect
                  exact
                  from={`/pages/${match.params.id}/results`}
                  to={`/pages/${match.params.id}/results/payments`}
                />
              )}

              {page.product.type === 'subscription' && (
                <Redirect
                  exact
                  from={`/pages/${match.params.id}/results`}
                  to={`/pages/${match.params.id}/results/subscriptions`}
                />
              )}

              {page.product.type === 'charge' && (
                <Route exact path={`/pages/${match.params.id}/results/payments`}>
                  <PagePayments page={page} />
                </Route>
              )}

              {page.product.type === 'subscription' && (
                <Route exact path={`/pages/${match.params.id}/results/subscriptions`}>
                  <PageSubscriptions page={page} />
                </Route>
              )}
            </>
          )}
        </Switch>
      </div>
    </>
  );
};

const PageResultsContainer = () => {
  const match = useRouteMatch();

  return (
    <PageProvider pageId={match.params.id}>
      <PageResults />
    </PageProvider>
  );
};

export default PageResultsContainer;
